import React, { useState } from "react"
import PropTypes from "prop-types"
import { Button, Dropdown } from "antd"
import { DownOutlined } from "@ant-design/icons"
import { useTranslation } from "react-i18next"
import { useDispatch } from "react-redux"
import BlackoutFormModal from "pagesV2/pro/Settings/Blackout/modals/BlackoutFormModal"
import { createBlackout } from "store/blackouts"
import { BLACKOUT_SCOPES, SCOPE_TAG } from "helpers/blackouts"

const SCOPES = [BLACKOUT_SCOPES.BUSINESS, BLACKOUT_SCOPES.LISTING, BLACKOUT_SCOPES.CREW, BLACKOUT_SCOPES.EQUIPMENT]

/**
 * "Add Blackout ▾" in the calendar header. Picks a scope, then opens the same form the
 * settings page uses — a blackout created here is the same record either way.
 */
const AddBlackoutButton = ({ isDisabled = false, isBlock = false, className = "", onCreated, onScopeSelected }) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const [scope, setScope] = useState(null)
  const [isSaving, setIsSaving] = useState(false)

  const items = SCOPES.map((value) => ({
    key: value,
    label: t(SCOPE_TAG[value].labelKey, { defaultValue: SCOPE_TAG[value].labelDefault }),
    onClick: () => {
      setScope(value)
      onScopeSelected?.()
    }
  }))

  const handleSubmit = async (payload) => {
    setIsSaving(true)
    try {
      await dispatch(createBlackout(payload))
      setScope(null)
      onCreated?.()
    } finally {
      setIsSaving(false)
    }
  }

  return (
    <>
      <Dropdown menu={{ items }} trigger={["click"]} disabled={isDisabled}>
        <Button
          color="primary"
          variant="outlined"
          shape="round"
          size="large"
          block={isBlock}
          className={className}
          disabled={isDisabled}
        >
          {t("calendar.blackout.add_blackout", { defaultValue: "Add Blackout" })}
          <DownOutlined />
        </Button>
      </Dropdown>

      {scope && (
        <BlackoutFormModal
          isOpen
          scope={scope}
          blackout={null}
          onCancel={() => setScope(null)}
          onSubmit={handleSubmit}
          isSaving={isSaving}
        />
      )}
    </>
  )
}

AddBlackoutButton.propTypes = {
  isDisabled: PropTypes.bool,
  /** Full-width trigger, for the mobile action sheet. */
  isBlock: PropTypes.bool,
  /** Lets a caller match the trigger to the buttons around it. */
  className: PropTypes.string,
  /** Called after a blackout is created so the calendar can refetch its feed. */
  onCreated: PropTypes.func,
  /** Called when a scope is picked — lets the mobile sheet close behind the modal. */
  onScopeSelected: PropTypes.func
}

export default AddBlackoutButton
